/* Silver Gardens — native single-plant pot carousel
 * Visual/navigation enhancement only. Never changes selected variants,
 * pot swatches, cart items, product prices or theme selection state.
 */
(() => {
  if (window.__SGNativePotCarouselLoaded) return;
  window.__SGNativePotCarouselLoaded = true;

  const rootSelector = '.sg-native-configurator';
  const arrowSVG = direction => direction === 'previous'
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>';

  function setup(root) {
    if (root.dataset.sgPotCarouselReady === 'true') return;
    const scroller = root.querySelector('.sg-native-pots');
    if (!scroller) return;
    root.dataset.sgPotCarouselReady = 'true';
    root.classList.add('sg-pot-carousel-ready');

    const controls = document.createElement('div');
    controls.className = 'sg-pot-carousel-controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Browse available plant pots');
    controls.hidden = true;
    const progress = document.createElement('div');
    progress.className = 'sg-pot-carousel-progress';
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', 'Pot options scroll position');
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('aria-valuemax', '100');
    progress.setAttribute('aria-valuenow', '0');
    const fill = document.createElement('span');
    fill.className = 'sg-pot-carousel-progress__fill';
    progress.appendChild(fill);
    const actions = document.createElement('div');
    actions.className = 'sg-pot-carousel-actions';
    const makeArrow = direction => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sg-pot-carousel-arrow';
      button.setAttribute('aria-label', direction === 'previous' ? 'Previous pot options' : 'More pot options');
      button.dataset.sgPotCarouselDirection = direction;
      button.innerHTML = arrowSVG(direction);
      return button;
    };
    const previous = makeArrow('previous');
    const next = makeArrow('next');
    actions.append(previous, next);
    controls.append(progress, actions);
    scroller.insertAdjacentElement('afterend', controls);

    let raf = 0;
    const visibleCards = () => [...scroller.querySelectorAll('.sg-native-pot-card-wrap:not([hidden])')].filter(el => getComputedStyle(el).display !== 'none');
    const maxScroll = () => Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    function update() {
      const max = maxScroll();
      const multipleOptions = visibleCards().length > 1;
      controls.hidden = !multipleOptions;
      if (!multipleOptions) return;
      const position = Math.min(max, Math.max(0, scroller.scrollLeft));
      const available = progress.clientWidth;
      const ratio = Math.min(1, Math.max(.14, scroller.clientWidth / scroller.scrollWidth));
      const thumbWidth = available * ratio;
      const progressX = max ? (position / max) * (available - thumbWidth) : 0;
      fill.style.width = (ratio * 100) + '%';
      fill.style.transform = 'translateX(' + progressX + 'px)';
      progress.setAttribute('aria-valuenow', String(Math.round(max ? (position / max) * 100 : 0)));
      previous.disabled = position <= 2;
      next.disabled = position >= max - 2;
    }
    function queueUpdate() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    }
    function focusSelectedIfOutside() {
      const active = scroller.querySelector('.sg-native-pot-card-wrap:not([hidden]) .sg-native-pot-card.is-active');
      const card = active?.closest('.sg-native-pot-card-wrap');
      if (!card) return;
      const viewport = scroller.getBoundingClientRect();
      const box = card.getBoundingClientRect();
      if (box.left < viewport.left - 3) scroller.scrollBy({ left: box.left - viewport.left, behavior: 'auto' });
      else if (box.right > viewport.right + 3) scroller.scrollBy({ left: box.right - viewport.right, behavior: 'auto' });
    }

    actions.addEventListener('click', event => {
      const button = event.target.closest('[data-sg-pot-carousel-direction]');
      if (!button || button.disabled) return;
      const cards = visibleCards();
      const first = cards[0];
      const second = cards[1];
      const stride = first && second ? Math.max(1, second.offsetLeft - first.offsetLeft) : (first?.offsetWidth || 92) + 9;
      const page = Math.max(stride, Math.floor(scroller.clientWidth / stride) * stride);
      const distance = button.dataset.sgPotCarouselDirection === 'next' ? page : -page;
      scroller.scrollBy({ left: distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });

    scroller.addEventListener('scroll', queueUpdate, { passive: true });
    scroller.addEventListener('click', event => {
      // Existing configurator handles actual pot selection. Follow it visually only.
      if (event.target.closest('[data-sg-native-pot],[data-sg-native-pot-colour]')) {
        requestAnimationFrame(() => { focusSelectedIfOutside(); queueUpdate(); });
      }
    });
    const observer = new MutationObserver(() => queueUpdate());
    observer.observe(scroller, { subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(queueUpdate) : null;
    resize?.observe(scroller);
    window.addEventListener('resize', queueUpdate, { passive: true });

    // Default selected pot can initially sit beyond the first visible four cards.
    requestAnimationFrame(() => {
      focusSelectedIfOutside();
      update();
    });
  }

  function mount() { document.querySelectorAll(rootSelector).forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
  // Mount again if Shopify swaps product content without reloading the page.
  if (typeof MutationObserver !== 'undefined') {
    const domObserver = new MutationObserver(records => {
      const changed = records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === 1 &&
        (node.matches?.(rootSelector) || node.querySelector?.(rootSelector))
      ));
      if (changed) requestAnimationFrame(mount);
    });
    const observe = () => {
      if (document.body) domObserver.observe(document.body, {childList:true,subtree:true});
    };
    if (document.body) observe();
    else document.addEventListener('DOMContentLoaded', observe, {once:true});
  }
})();
